import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
  theme,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { ApiService } from "../../api";
import type { Delegation, DelegationScope } from "../../api/models/Delegation";
import type { UserList } from "../../api/models/UserList";
import { apiErrorMessage } from "../../api/apiError";
import { PageHeader } from "../../components/PageHeader";
import { space } from "../../theme/space";

/**
 * The signed-in person's id, for splitting "cover for me" from "cover I give".
 *
 * Read straight from the stored profile rather than through `currentProfile`,
 * which deliberately carries only the fields a form default needs. Nothing is
 * *authorised* with it -- the server decides whose rows come back and whose may
 * be touched; this only decides which of two tables a row belongs in.
 */
const myId = (): string | undefined => {
  try {
    return JSON.parse(localStorage.getItem("userInfo") || "{}")?.id;
  } catch {
    return undefined;
  }
};

/**
 * Cover: "I am away from Friday -- Anna can act for me" (D2).
 *
 * **One screen for both directions, and that is the design.** The two questions
 * a person has here are "who is covering for me?" and "whose work am I
 * holding?", and the second is what explains the extra rows in their inbox. Two
 * screens would separate a fact from its explanation.
 *
 * What the page has to keep saying, in three places, is that **nothing moves**.
 * The permits stay with their owner, the deadlines keep running, and ending
 * cover takes everything back the moment it is ended -- because nothing was
 * handed over, only shared. A stand-in who thinks the work has been transferred
 * to them behaves differently from one who knows they are covering.
 */

const SCOPES: { value: DelegationScope; label: string; hint: string }[] = [
  {
    value: "All",
    label: "Everything",
    hint: "Signatures and form sections alike.",
  },
  {
    value: "Approvals",
    label: "Signatures only",
    hint: "They can sign for you, but not fill your sections in.",
  },
  {
    value: "Sections",
    label: "Form sections only",
    hint: "They can fill your sections in, but not sign for you.",
  },
];

export const DelegationsPage = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const me = myId();
  const [rows, setRows] = useState<Delegation[]>([]);
  const [people, setPeople] = useState<UserList[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [delegate, setDelegate] = useState<string | undefined>();
  const [scope, setScope] = useState<DelegationScope>("All");
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [reason, setReason] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    ApiService.apiAccountsDelegationsList()
      .then((result: any) => setRows(result?.results ?? result ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);
  useEffect(() => {
    ApiService.apiAccountsUsersList()
      .then(setPeople)
      .catch(() => setPeople([]));
  }, []);

  const mine = rows.filter((row) => row.delegator === me);
  const theirs = rows.filter((row) => row.delegator !== me);

  const arrange = async () => {
    if (!delegate) return;
    setSaving(true);
    try {
      await ApiService.apiAccountsDelegationsCreate({
        // No `delegator`: the server takes the caller, which is the only
        // person this screen can arrange cover for anyway.
        delegate,
        scope,
        // An unset start means "from now", which is what somebody arranging
        // cover on the day they leave means. Sent explicitly only when they
        // chose a date, so the server's own default stays the single answer.
        ...(range?.[0] ? { starts_at: range[0]!.toISOString() } : {}),
        ends_at: range?.[1] ? range[1]!.toISOString() : null,
        reason,
      });
      message.success(t("Cover arranged"));
      setOpen(false);
      setDelegate(undefined);
      setReason("");
      setRange(null);
      load();
    } catch (error) {
      // The server's refusals here are written as sentences a person can act on
      // ("X has already passed their own work on to Y"), so they are shown
      // rather than swallowed.
      message.error(apiErrorMessage(error, t("Could not arrange cover.")));
    } finally {
      setSaving(false);
    }
  };

  const end = (row: Delegation) => {
    Modal.confirm({
      title: t("End this cover?"),
      content: t(
        "{{name}} stops seeing your work immediately. Nothing they already did is undone, and nothing has to be handed back — the work never left you.",
        { name: row.delegate_name }
      ),
      okText: t("End cover"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await ApiService.apiAccountsDelegationsDelete(row.id);
          message.success(t("Cover ended"));
          load();
        } catch (error) {
          message.error(apiErrorMessage(error, t("Could not end this cover.")));
        }
      },
    });
  };

  const period = (row: Delegation) => {
    const from = dayjs(row.starts_at).format("D MMM YYYY");
    const to = row.ends_at ? dayjs(row.ends_at).format("D MMM YYYY") : null;
    return to ? `${from} — ${to}` : t("{{from}} — until ended", { from });
  };

  const state = (row: Delegation) => {
    if (row.is_live) return <Tag color="green">{t("In force")}</Tag>;
    if (!row.is_active) return <Tag>{t("Ended")}</Tag>;
    if (dayjs(row.starts_at).isAfter(dayjs()))
      return <Tag color="blue">{t("Starts later")}</Tag>;
    return <Tag>{t("Expired")}</Tag>;
  };

  const scopeLabel = (value: DelegationScope) =>
    t(SCOPES.find((s) => s.value === value)?.label ?? value);

  const columns = (outgoing: boolean) => [
    {
      title: outgoing ? t("Covered by") : t("Covering for"),
      key: "who",
      render: (_: unknown, row: Delegation) =>
        outgoing ? row.delegate_name : row.delegator_name,
    },
    {
      title: t("Covers"),
      key: "scope",
      render: (_: unknown, row: Delegation) => scopeLabel(row.scope),
    },
    { title: t("Period"), key: "period", render: (_: unknown, row: Delegation) => period(row) },
    {
      title: t("Reason"),
      key: "reason",
      render: (_: unknown, row: Delegation) =>
        row.reason || <span style={{ color: token.colorTextSecondary }}>—</span>,
    },
    { title: t("Status"), key: "state", render: (_: unknown, row: Delegation) => state(row) },
    ...(outgoing
      ? [
          {
            title: t("Actions"),
            key: "actions",
            width: 120,
            render: (_: unknown, row: Delegation) =>
              row.is_active ? (
                <Button size="small" type="text" danger onClick={() => end(row)}>
                  {t("End")}
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title={t("Cover while I am away")}
        meta={t(
          "Nothing is transferred: your permits stay yours and their deadlines keep running. Cover lets somebody act on them until you end it."
        )}
        actions={
          <Button type="primary" onClick={() => setOpen(true)}>
            {t("Arrange cover")}
          </Button>
        }
      />

      <div style={{ marginBottom: space.base }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: `0 0 ${space.tight}px` }}>
          {t("People covering for me")}
        </h3>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={mine}
          columns={columns(true) as any}
          pagination={false}
          locale={{ emptyText: t("Nobody is covering for you.") }}
        />
      </div>

      {theirs.length > 0 && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: `0 0 ${space.tight}px` }}>
            {t("Work I am covering")}
          </h3>
          {/* Where the extra rows in their inbox come from, said once, here. */}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: space.tight }}
            message={t(
              "These people's tasks appear in My Actions marked with their name. Anything you do is recorded as done by you on their behalf."
            )}
          />
          <Table
            rowKey="id"
            size="small"
            dataSource={theirs}
            columns={columns(false) as any}
            pagination={false}
          />
        </div>
      )}

      <Modal
        open={open}
        title={t("Arrange cover")}
        okText={t("Arrange cover")}
        cancelText={t("Cancel")}
        confirmLoading={saving}
        okButtonProps={{ disabled: !delegate }}
        onOk={arrange}
        onCancel={() => setOpen(false)}
      >
        <Space direction="vertical" size={space.tight} style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 4 }}>{t("Who covers for you")}</div>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              value={delegate}
              onChange={setDelegate}
              placeholder={t("Choose a colleague")}
              options={people
                .filter((person) => person.id !== me)
                .map((person) => ({ value: person.id, label: person.name }))}
            />
          </div>

          <div>
            <div style={{ marginBottom: 4 }}>{t("What they can do")}</div>
            <Radio.Group
              value={scope}
              onChange={(event) => setScope(event.target.value)}
            >
              <Space direction="vertical" size={2}>
                {SCOPES.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {t(option.label)}{" "}
                    <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>
                      — {t(option.hint)}
                    </span>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>

          <div>
            <div style={{ marginBottom: 4 }}>
              {t("From and until")}{" "}
              <Tooltip
                title={t(
                  "Leave the end date empty for cover that runs until you end it yourself."
                )}
              >
                <span style={{ color: token.colorTextSecondary }}>ⓘ</span>
              </Tooltip>
            </div>
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              allowEmpty={[true, true]}
              showTime
              value={range as any}
              onChange={(value) => setRange(value as any)}
            />
          </div>

          <div>
            <div style={{ marginBottom: 4 }}>{t("Reason (optional)")}</div>
            <Input
              value={reason}
              maxLength={255}
              placeholder={t("Annual leave")}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>

          {/* Said at the moment of arranging, not only afterwards: somebody who
              believes they are handing work over would otherwise stop watching
              a permit that is still theirs. */}
          <Alert
            type="info"
            showIcon
            message={t(
              "Your permits stay yours. They will see your outstanding work and can act on it; every action records that they did it on your behalf. Signatures are only covered on steps whose template allows it."
            )}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default DelegationsPage;
